
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/components/ui/use-toast';
import MobileLayout from '@/components/layout/MobileLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

type ResetStatus = 'idle' | 'loading' | 'success' | 'error';

const PasswordReset: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [status, setStatus] = useState<ResetStatus>('idle');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Check if we have a valid hash in the URL (Supabase handles this automatically)
  useEffect(() => {
    const checkSession = async () => {
      const { data, error } = await supabase.auth.getSession();
      
      // If no active session and not in the middle of a password reset flow
      if (!data.session && status === 'idle') {
        setStatus('error');
        setErrorMessage('Invalid or expired password reset link. Please request a new password reset link.');
      }
    };
    
    checkSession();
  }, [status]);

  const handleResetPassword = async () => {
    // Validate passwords
    if (password !== passwordConfirm) {
      toast({
        title: "Passwords don't match",
        description: "Please make sure your passwords match.",
        variant: "destructive",
      });
      return;
    }

    if (password.length < 6) {
      toast({
        title: "Password too short",
        description: "Your password must be at least 6 characters long.",
        variant: "destructive",
      });
      return;
    }

    try {
      setStatus('loading');

      // Update the password
      const { error } = await supabase.auth.updateUser({
        password
      });

      if (error) {
        throw error;
      }

      setStatus('success');
      toast({
        title: "Password reset successful",
        description: "Your password has been reset. You can now log in with your new password.",
      });

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error("Error resetting password:", error);
      setStatus('error');
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "An error occurred. Please try again or request a new reset link."
      );
    }
  };

  return (
    <MobileLayout hideNavigation>
      <div className="flex flex-col items-center justify-center min-h-screen p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 pb-8 px-6">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold mb-2">Reset Your Password</h1>
              
              {/* Success State */}
              {status === 'success' && (
                <div className="flex flex-col items-center justify-center text-center p-4">
                  <div className="bg-green-100 dark:bg-green-900/20 rounded-full p-3 mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600 dark:text-green-400" />
                  </div>
                  <h2 className="text-xl font-medium mb-2">Password Reset Successful</h2>
                  <p className="text-muted-foreground mb-4">
                    Your password has been successfully updated. You will be redirected to the login page shortly.
                  </p>
                  <Button 
                    onClick={() => navigate('/login')} 
                    className="w-full"
                  >
                    Go to Login
                  </Button>
                </div>
              )}
              
              {/* Error State */}
              {status === 'error' && (
                <div className="flex flex-col items-center justify-center text-center p-4">
                  <div className="bg-red-100 dark:bg-red-900/20 rounded-full p-3 mb-4">
                    <AlertCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
                  </div>
                  <h2 className="text-xl font-medium mb-2">Reset Failed</h2>
                  <p className="text-muted-foreground mb-4">
                    {errorMessage || "An error occurred while resetting your password."}
                  </p>
                  <Button 
                    onClick={() => navigate('/forgot-password')} 
                    variant="outline" 
                    className="w-full"
                  >
                    Try Again
                  </Button>
                </div>
              )}
              
              {/* Form State */}
              {(status === 'idle' || status === 'loading') && (
                <>
                  {status === 'idle' && (
                    <p className="text-muted-foreground">
                      Please enter your new password below.
                    </p>
                  )}
                  
                  <div className="space-y-4 mt-6">
                    <div className="space-y-2">
                      <Label htmlFor="password">New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="password"
                          type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="New password"
                          disabled={status === 'loading'}
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          disabled={status === 'loading'}
                        >
                          {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="confirm-password">Confirm New Password</Label>
                      <div className="relative">
                        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <Input
                          id="confirm-password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          value={passwordConfirm}
                          onChange={(e) => setPasswordConfirm(e.target.value)}
                          placeholder="Confirm new password"
                          disabled={status === 'loading'}
                          className="pl-10 pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                          disabled={status === 'loading'}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                    
                    <Button
                      onClick={handleResetPassword}
                      className="w-full"
                      disabled={!password || !passwordConfirm || status === 'loading'}
                    >
                      {status === 'loading' ? "Resetting..." : "Reset Password"}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </MobileLayout>
  );
};

export default PasswordReset;
